export const T = {
  en: {
    nav: {
      villa: "The Villa",
      experiences: "Experiences",
      dining: "Eat & Drink",
      blog: "What's Hot",
      services: "Island Services",
      signin: "Sign in",
      enquire: "Enquire",
      enquireMobile: "Enquire about the villa",
      myTrips: "My trips",
      ownerPanel: "Owner panel",
      signout: "Sign out",
    },
    hero: {
      caption: "Cabarete · Dominican Republic",
      lines: ["Your private", "paraíso in", "Cabarete."],
      sub: "A peaceful three-bedroom villa in Cabarete, with a private pool and the beach nearby, nestled in a quiet gated community — your own little corner of Caribbean paradise.",
      enquire: "Enquire about the villa",
      explore: "Explore experiences",
      stats: [
        { value: "3", label: "Bedrooms" },
        { value: "2.5", label: "Bathrooms" },
        { value: "6", label: "Guests" },
        { value: "5 min", label: "To the sand" },
      ],
    },
    marquee: {
      items: [
        "Kitesurf capital of the Caribbean",
        "Private pool, open 24/7",
        "Chef service on request",
        "27 waterfalls, 40 minutes away",
        "Barefoot beach bars",
        "Trade winds 300 days a year",
      ],
      items2: ["Bonfires & bachata on the strip", "Sunset sails every Friday", "Ask us anything — we live here"],
    },
    villa: {
      caption: "The villa",
      title: "Salt air, quiet streets, and a pool all to yourselves.",
      copy: "Villa Paraiso sits in a quiet gated community just five minutes from Cabarete's beach. Mornings are for coffee by your private pool; afternoons for Kite Beach; evenings for whatever the town feels like — we'll point you there.",
      cta: "Check availability",
      amenities: [
        "Private pool — open 24/7",
        "Beach 5 minutes away",
        "Full kitchen",
        "Fast Wi-Fi + workspace",
        "Smart TV with Netflix",
        "Free parking",
        "A/C throughout",
        "Gated community security",
      ],
    },
    gallery: {
      hint: "Tap a photo to wander through the villa",
      captions: [
        "Villa Paraiso — your corner of the community",
        "Green lawn, blue sky, total quiet",
        "The bright open living room",
        "From the sofa to the kitchen — all yours",
        "Dinner for six under the skylight",
      ],
    },
    concierge: {
      title: "Ask Paraiso",
      subtitle: "Your Cabarete concierge",
      greeting: "Hola! I'm Paraiso, the villa's concierge. Ask me anything — the villa, excursions, where to eat tonight, or what Cabarete does best.",
      placeholder: "Ask about the villa, kitesurfing, dinner…",
      send: "Send",
      error: "The concierge is resting — try again in a moment.",
    },
    faq: {
      caption: "Good to know",
      title: "Questions, answered.",
      items: [
        { q: "What are the check-in and check-out times?", a: "Check-in from 3:00 PM, check-out by 11:00 AM. Arriving early or leaving late? Email us — we're flexible whenever the calendar allows." },
        { q: "Is the pool really private?", a: "Yes — the pool is yours alone, open 24/7. No sharing, no schedule, no strangers. Midnight swims encouraged." },
        { q: "How far is the beach?", a: "About 5 minutes. Cabarete Bay for swimming and dinner on the sand, Kite Beach for the world-famous wind — both an easy stroll." },
        { q: "Is the area safe?", a: "The villa sits inside a quiet gated community with exterior security cameras and controlled access. Most guests tell us they sleep with the windows open." },
        { q: "How do payments work?", a: "You pay the full amount online via Stripe's secure checkout — quick, encrypted, and nothing is left to settle on arrival. Payments are non-refundable — full details in our Terms & Conditions." },
        { q: "Can you arrange airport pickup?", a: "Yes — Puerto Plata airport (POP) is about 25 minutes away. Email us your flight details and we'll have a trusted driver waiting." },
        { q: "Do I need experience to kitesurf?", a: "Not at all. Cabarete is where beginners learn — our discovery lessons pair you with certified instructors, and all gear is included." },
        { q: "Is the Wi-Fi good enough for remote work?", a: "Yes — fast fiber internet plus a dedicated workspace. Many guests work mornings and kite afternoons." },
        { q: "Can we book the private chef or a massage?", a: "Absolutely — Island Table (private chef) and Island Serenity (massage) are available right at the villa. Request them on the Island Services page and we'll reply with a tailored quote." },
        { q: "What if something comes up and we can't travel?", a: "Contact us as early as possible by email. Payments are non-refundable, but where we can re-rent your dates we'll happily offer to move your stay once." },
      ],
    },
    terms: {
      caption: "The fine print",
      title: "Terms & Conditions",
      updated: "Last updated: October 2026",
      intro: "Welcome to Villa Paraiso. By booking a stay, excursion or island service through this website, you agree to the following terms, which exist to keep the villa beautiful and every guest happy.",
      sections: [
        { h: "1. Bookings & payments", b: "A booking is confirmed once the full amount shown at checkout is received through our secure payment processor (Stripe). Payments are NON-REFUNDABLE under all circumstances, including changes of plans, travel disruptions, illness or weather. Rates are quoted in US dollars." },
        { h: "2. Cancellations & date changes", b: "Because dates are held exclusively for you, cancellations forfeit the amount paid. A one-time date change may be offered, at our discretion, if requested at least 14 days before check-in and subject to availability. No-shows are charged in full." },
        { h: "3. Damage, fines & security", b: "Guests are financially responsible for any damage, breakage or excessive mess caused during the stay, beyond normal wear. Repair or replacement costs will be invoiced to the guest, and a fine of up to USD 250 may apply for serious negligence or breach of house rules. Exterior security cameras monitor the property's entrances for everyone's safety; no cameras exist inside the home." },
        { h: "4. Occupancy, events & noise", b: "Maximum occupancy is 6 registered guests. Parties, events and unregistered overnight visitors require prior written approval. The villa is inside a quiet gated community — please respect quiet hours from 10:00 PM to 8:00 AM. Breaches may lead to termination of the stay without refund." },
        { h: "5. Pool & property use", b: "The pool is unguarded and used entirely at your own risk. Children must be supervised at all times. No diving, no glass near the pool. Smoking is permitted outdoors only; indoor smoking incurs a USD 250 cleaning fine." },
        { h: "6. Excursions & island services", b: "Excursions, chef and massage services may be operated by independent third parties. Villa Paraiso arranges these in good faith but is not liable for their operation, schedule changes, or weather-related cancellations; in such cases amounts paid to us for the affected service are refunded or credited." },
        { h: "7. Liability", b: "Villa Paraiso is not liable for loss, theft, injury or accidents occurring on the property or during activities, except where caused by our proven gross negligence. Guests are responsible for their travel insurance, documents and belongings." },
        { h: "8. Force majeure", b: "We are not liable for failures caused by events beyond our control — storms, power or internet outages, or government restrictions. We will always work with you to reschedule or credit affected services." },
        { h: "9. Privacy & contact", b: "Your details are used only to manage your booking and communicate with you — never sold or shared. These terms are governed by the laws of the Dominican Republic. Questions? Email villaparaisocabarete@gmail.com." },
      ],
    },
    services: {
      caption: "Island services",
      title: "A little more island, arranged for you.",
      copy: "Two quiet luxuries, available right at the villa. Everything is priced upon request — send your dates and we'll reply personally with a tailored quote.",
      priceNote: "Price upon request",
      teaserCaption: "At the villa",
      teaserCta: "Explore island services",
      guestsSuffix: "guests",
      requestBtn: "Request this experience",
      chef: {
        name: "Island Table — Private Chef",
        tagline: "Relax into island life while your private chef takes care of the cooking.",
        gatheringLbl: "Your gathering",
        experienceLbl: "Choose your experience",
        gatherings: { two: "Table for Two", together: "Taste & Togetherness", gathering: "Island Gathering" },
        experiences: {
          breakfast: "Caribbean Sunrise — Breakfast only",
          lunch: "Midday in Paradise — Lunch only",
          dinner: "Tropical Evening — Dinner only",
          fullday: "A Full Day of Island Flavors — Breakfast, lunch & dinner",
        },
      },
      massage: {
        name: "Island Serenity — Massage",
        tagline: "Slow down, unwind, and enjoy a little Caribbean calm.",
        durationLbl: "Choose your pause",
        durations: { pause: "Island Pause · 30 minutes", escape: "Paradise Escape · 1 hour" },
      },
      modal: {
        title: "Request island service",
        date: "Preferred date",
        name: "Your name",
        namePh: "Full name",
        email: "Email",
        phone: "Phone (optional)",
        notes: "Anything we should know?",
        notesPh: "Allergies, celebrations, dietary wishes…",
        submit: "Send request",
        sending: "Sending…",
        doneTitle: "Request sent",
        doneCopy: "The owners will reply personally with a tailored quote — usually within a day. A confirmation is on its way to your inbox.",
        summary: "Your request",
        close: "Done",
        total: "Total",
        payFull: "Pay online — ${amount}",
        orWait: "Or simply wait — the owners will confirm by email and nothing is charged yet.",
      },
    },
    availability: {
      caption: "Availability & rates",
      titleA: "Golden dates",
      titleB: "go fast.",
      perNight: "/ night",
      minStay: "Minimum stay of {n} nights",
      minStay1: "Minimum stay: 1 night",
      depositLine: "Pay in full online to secure your dates",
      crossedOut: "Crossed-out dates are already reserved",
      cta: "Enquire for your dates",
      from: "from",
      seasonal: "Seasonal rates apply on selected dates",
    },
    homeExp: {
      caption: "Experiences",
      title: "Adventures booked through us, picked up at your door.",
      viewAll: "View all experiences",
    },
    dining: {
      caption: "Eat & drink",
      title: "Our table is booked. Yours should be too.",
      viewAll: "The full guide",
    },
    homeBlog: {
      caption: "What's hot",
      title: "Notes from the beach — nightlife, wind, waterfalls.",
      viewAll: "Read the journal",
    },
    cta: {
      titleA: "Ready for",
      titleB: "golden hour?",
      sub: "Send us your dates and we'll come back with availability, a rate, and a loose plan for your first sunset.",
      enquire: "Enquire about the villa",
      dine: "Where to eat tonight",
    },
    footer: {
      tagline: "A private Caribbean hideaway in Cabarete, Dominican Republic — with every excursion, table and sunset arranged by your host.",
      explore: "Explore",
      contact: "Contact the host",
      rights: "Villa Paraiso. All rights reserved.",
      quote: "Barefoot luxury, on the edge of the Atlantic.",
      faq: "FAQ",
      terms: "Terms & Conditions",
    },
    excursions: {
      caption: "Experiences",
      titleA: "Choose the adventure.",
      titleB: "We handle the rest.",
      copy: "Every experience is run by operators we know personally and booked directly through your host. Pick a date and time, and we'll collect you at the Villa Paraiso entrance — or at your hotel if you're visiting from elsewhere in Cabarete.",
      loading: "Loading experiences…",
      all: "All",
      noteTitle: "Staying at Villa Paraiso?",
      noteCopy: "Every excursion includes door-to-door coordination: we confirm the operator, the pick-up time lands in your messages, and someone is waiting at the villa gate when the van arrives. No logos, no queues, no tourist-bus energy.",
      book: "Book this experience",
      perPerson: "/ person",
    },
    restaurants: {
      caption: "Eat & drink · Cabarete",
      titleA: "Where we'd take you,",
      titleB: "if you were family.",
      copy: "No paid placements — just the tables we keep going back to, from sand-floor seafood shacks to candlelit fine dining. Ask your host for tonight's pick and we'll call ahead.",
      loading: "Setting the tables…",
      all: "All",
      order: "Order:",
      directions: "Get directions",
    },
    blog: {
      caption: "What's hot",
      titleA: "The Cabarete",
      titleB: "journal.",
      copy: "What's on, what's opening, and where the wind is blowing — written by your host, updated through the season.",
      loading: "Fetching the latest…",
      readStory: "Read the story →",
      back: "The journal",
      keepReading: "Keep reading",
      notFound: "This story drifted out to sea.",
      notFoundBack: "← Back to the journal",
      opening: "Opening the story…",
    },
    auth: {
      signIn: "Sign in",
      createAccount: "Create account",
      name: "Full name",
      namePh: "Your name",
      email: "Email",
      password: "Password",
      passwordPh: "6+ characters",
      guestAccount: "Guest account",
      submitLogin: "Sign in",
      submitRegister: "Create account",
      google: "Continue with Google",
      orDivider: "or with email",
      wait: "One moment…",
      ownerNote: "Owner of the villa? Sign in with your admin account to open the owner panel.",
      backHome: "← Back to the villa",
      welcomeBack: "Welcome back to the villa",
      accountCreated: "Account created — welcome to Villa Paraiso",
      sideCaption: "Members of the house",
      sideTitle: "Sign in to book adventures and keep every sunset in one place.",
    },
    booking: {
      title: "Book your experience",
      bookedTitle: "You're booked",
      date: "Date",
      guests: "Guests",
      timeSlot: "Time slot",
      pickup: "Pick-up location",
      villaOption: "Villa Paraiso",
      villaOptionSub: "We collect you right at the villa's main entrance.",
      customOption: "Hotel / elsewhere",
      customOptionSub: "Staying at a hotel or resort in Cabarete? We'll come to you.",
      yourHotel: "Your hotel / pick-up spot",
      hotelPh: "e.g. Hotel Velero Beach, main lobby",
      contactName: "Contact name",
      contactNamePh: "Lead guest",
      phone: "Phone",
      notes: "Special requests",
      notesPh: "Vegetarian lunch, nervous swimmer, birthday surprise…",
      summary: "Summary",
      seasonUplift: "{season} pricing +{pct}%",
      orDepositNow: "or pay ${amount} in full now",
      nothingCharged: "Nothing is charged yet — after confirming you can pay the full amount online, securely.",
      continue: "Continue",
      back: "Back",
      confirm: "Confirm booking",
      confirming: "Confirming…",
      pass: "Adventure pass",
      ref: "Ref",
      dateTime: "Date & time",
      guestsLbl: "Guests",
      people: "people",
      person: "person",
      pickupLbl: "Pick-up",
      total: "Total",
      statusPending: "pending — host confirms shortly",
      emailNote: "A confirmation email is on its way to you and the host. Lock in your spot by paying in full online.",
      payDeposit: "Pay in full — ${amount}",
      openingCheckout: "Opening checkout…",
      whatsapp: "WhatsApp your host",
      viewTrips: "View my trips",
      done: "Done",
      errDate: "Pick a date for your adventure",
      errTime: "Choose a time slot",
      errHotel: "Tell us your hotel or pick-up spot",
      errContact: "We need a contact name and phone number",
      signinToBook: "Please sign in to complete your booking",
    },
    enquiry: {
      title: "Book the Villa",
      doneTitle: "Enquiry sent",
      intro: "Pick your dates and book instantly — or send us a note if you'd like a personal touch first.",
      name: "Full name",
      namePh: "Your name",
      email: "Email",
      checkin: "Check-in",
      checkout: "Check-out",
      guests: "Guests",
      phone: "Phone (optional)",
      notes: "Anything we should know?",
      notesPh: "Anniversary trip, airport pickup, chef nights…",
      send: "Send enquiry",
      sending: "Sending…",
      nightsTimes: "{nights} nights × ${rate}/night",
      overlap: "These dates overlap an existing reservation — please choose others.",
      belowMin: "Minimum stay is {n} nights.",
      thanks: "Gracias, {name}.",
      successCopy: "is with the host. A confirmation email is on its way to",
      guestsWord: "guests",
      nightsWord: "nights",
      estimated: "Estimated stay total:",
      whatsapp: "WhatsApp your host",
      backToParadise: "Back to paradise",
      errDates: "Please choose your check-in and check-out dates",
      errOrder: "Check-out must be after check-in",
      errMin: "Minimum stay is {n} nights",
      errOverlap: "Those dates overlap an existing reservation — try different dates",
      waText: "Hola Villa Paraiso! I just sent a villa enquiry.",
    },
    dashboard: {
      caption: "My trips",
      hola: "Hola,",
      sub: "Every adventure and enquiry, in one sunny place.",
      loading: "Loading your trips…",
      bookingsTitle: "Excursion bookings",
      emptyBookings: "No adventures yet. The waterfalls are waiting.",
      browse: "Browse experiences",
      enquiriesTitle: "Villa enquiries",
      emptyEnquiries: "No villa enquiries yet. Your dates, our pool.",
      seeVilla: "See the villa",
      cancel: "Cancel",
      cancelled: "Booking cancelled — the host has been notified",
      payDeposit: "Pay in full — ${amount}",
      payStayDeposit: "Pay stay in full — ${amount}",
      openingCheckout: "Opening checkout…",
      depositPaid: "Paid",
      depositRefunded: "Refunded",
      chatHost: "Chat with host",
      guests: "guests",
      nights: "nights",
      sent: "sent",
      estimate: "Estimate",
      waBooking: "Hola Villa Paraiso! About my booking {ref} — {title} on {date}.",
      waStay: "Hola Villa Paraiso! About my stay {checkin} → {checkout}.",
    },
    reviews: {
      caption: "Guest book",
      title: "Loved by the people who stayed.",
      subtitle: "Notes from past guests — unedited, approved by the host.",
      share: "Share your experience",
      shareTitle: "Leave a review",
      yourRating: "Your rating",
      yourReview: "Your review",
      reviewPh: "The pool at golden hour, the host's recommendations…",
      submit: "Submit review",
      thanks: "Gracias! Your review will appear once the host approves it.",
      needStay: "Reviews are for guests with a booking or stay enquiry.",
      hostReply: "Response from your host",
      addPhoto: "Add a photo (optional)",
      uploadingPhoto: "Uploading photo…",
      photoReady: "Photo attached",
      removePhoto: "Remove",
    },
    payment: {
      checking: "Confirming your payment…",
      checkingSub: "Talking to the payment provider. One moment.",
      paidA: "Payment",
      paidB: "received.",
      paidSub: "Your booking is locked in. A receipt is on its way to your inbox, and your host has been notified.",
      pendingTitle: "Almost there",
      pendingSub: "The payment is still processing. It usually lands within a minute — check \"My trips\" shortly, and we'll email you the moment it's confirmed.",
      cancelledTitle: "No charge was made",
      cancelledSub: "You cancelled the checkout. Your booking is still held — you can pay anytime from \"My trips\".",
      failedTitle: "We couldn't find that payment",
      failedSub: "The session link looks incomplete. Check \"My trips\" for your payment status.",
      viewTrips: "View my trips",
      goTrips: "Go to my trips",
      backTrips: "Back to my trips",
    },
    cart: {
      title: "Your cart",
      empty: "Your cart is empty",
      emptySub: "Add an excursion, a service or a villa stay — it will wait for you here.",
      browse: "Browse experiences",
      addBtn: "Add to cart",
      added: "Added to your cart",
      removed: "Removed from cart",
      guests: "Guests",
      quote: "Quote on request",
      approval: "Host approval first",
      subtotal: "Total due today",
      feesNote: "No extra fees — the price you see is the price you pay.",
      requestsNote: "Quote-on-request services are sent to the host at checkout — you won't be charged for those today.",
      checkout: "Secure checkout",
      checkingOut: "Opening secure checkout…",
      bookNow: "Book now",
      sendRequests: "Send requests",
      requestsSent: "Requests sent — the host will reply by email",
      signin: "Sign in to check out",
      phone: "Phone (for the host)",
      unavailable: "Unavailable",
      continueShopping: "Continue shopping",
      orderTitle: "Your order",
      villaStay: "Villa Paraiso stay",
    },
    food: {
      caption: "Private dining",
      title: "Private Dining & Food Services",
      intro: "Enjoy freshly prepared meals in the comfort of Villa Paraiso. From relaxed breakfasts to Dominican dinners and private BBQ evenings, let us take care of the cooking while you enjoy your stay.",
      perPerson: "per person",
      perPersonDay: "per person, per day",
      quote: "Request a quote",
      conditionsTitle: "Good to know",
      c1: "Minimum charge equivalent to four guests per meal or daily package.",
      c2: "Standard ingredients, meal preparation, serving and kitchen cleanup included.",
      c3: "Alcohol, premium ingredients and special requests cost extra and require a confirmed quote.",
      c4: "Requests must be submitted at least 48 hours in advance.",
      c5: "All services are subject to availability and host confirmation.",
      c6: "Please disclose all allergies and dietary requirements in advance — we can only accommodate what we know about.",
      c7: "Any applicable taxes or mandatory fees are itemized in your confirmed quote — before any payment.",
      requestBtn: "Request Food Service",
      modalTitle: "Request food service",
      name: "Full name",
      email: "Email",
      whatsapp: "WhatsApp number",
      ref: "Reservation reference (if you have one)",
      service: "Service or package",
      dateSingle: "Service date",
      dateFrom: "First day",
      dateTo: "Last day",
      times: "Preferred meal times",
      adults: "Adults",
      children: "Children",
      allergies: "Allergies, dietary preferences & special requests",
      estimate: "Estimated total",
      minNote: "Four-guest minimum applies — billed for {billed} guests.",
      quoteReq: "Quote required — we'll confirm pricing by email.",
      submit: "Send request",
      sending: "Sending…",
      received: "Your food service request has been received. We'll confirm availability, menu and final pricing before payment.",
      done: "Request received",
      addMeals: "Add meals to your stay",
      requestFor: "Request food service",
      advanceNote: "At least 48 hours in advance, please.",
      back: "Back",
      guestsSuffix: "guests",
    },
  },
  es: {
    nav: {
      villa: "La Villa",
      experiences: "Experiencias",
      dining: "Comer & Beber",
      blog: "Lo Nuevo",
      services: "Servicios Isleños",
      signin: "Entrar",
      enquire: "Consultar",
      enquireMobile: "Consulta por la villa",
      myTrips: "Mis viajes",
      ownerPanel: "Panel del dueño",
      signout: "Salir",
    },
    hero: {
      caption: "Cabarete · República Dominicana",
      lines: ["Tu paraíso", "privado en", "Cabarete."],
      sub: "Una villa tranquila de tres habitaciones en Cabarete, con piscina privada y la playa cerca, en una comunidad cerrada y segura — tu propio rincón del paraíso caribeño.",
      enquire: "Consulta por la villa",
      explore: "Explora experiencias",
      stats: [
        { value: "3", label: "Habitaciones" },
        { value: "2.5", label: "Baños" },
        { value: "6", label: "Huéspedes" },
        { value: "5 min", label: "A la arena" },
      ],
    },
    marquee: {
      items: [
        "Capital del kitesurf del Caribe",
        "Piscina privada, abierta 24/7",
        "Chef privado bajo petición",
        "27 cascadas a 40 minutos",
        "Bares playeros a pie descalzo",
        "Vientos alisios 300 días al año",
      ],
      items2: ["Fogatas y bachata en el malecón", "Veleros al atardecer cada viernes", "Pregúntanos lo que sea — vivimos aquí"],
    },
    villa: {
      caption: "La villa",
      title: "Aire salado, calles tranquilas y una piscina solo para ustedes.",
      copy: "Villa Paraiso está en una comunidad cerrada y tranquila, a solo cinco minutos de la playa de Cabarete. Las mañanas son para el café junto a tu piscina privada; las tardes para Kite Beach; las noches para lo que el pueblo proponga — nosotros te guiamos.",
      cta: "Ver disponibilidad",
      amenities: [
        "Piscina privada — abierta 24/7",
        "Playa a 5 minutos",
        "Cocina completa",
        "Wi-Fi rápido + espacio de trabajo",
        "Smart TV con Netflix",
        "Estacionamiento gratis",
        "A/C en toda la villa",
        "Comunidad cerrada con seguridad",
      ],
    },
    gallery: {
      hint: "Toca una foto para recorrer la villa",
      captions: [
        "Villa Paraiso — tu rincón de la comunidad",
        "Césped verde, cielo azul, silencio total",
        "La sala abierta y luminosa",
        "Del sofá a la cocina — todo tuyo",
        "Cena para seis bajo el tragaluz",
      ],
    },
    concierge: {
      title: "Pregunta a Paraiso",
      subtitle: "Tu concierge de Cabarete",
      greeting: "¡Hola! Soy Paraiso, el concierge de la villa. Pregúntame lo que quieras — la villa, las excursiones, dónde cenar hoy o lo mejor de Cabarete.",
      placeholder: "Pregunta por la villa, el kitesurf, la cena…",
      send: "Enviar",
      error: "El concierge está descansando — inténtalo en un momento.",
    },
    faq: {
      caption: "Bueno saberlo",
      title: "Preguntas, respondidas.",
      items: [
        { q: "¿Cuáles son los horarios de llegada y salida?", a: "Llegada desde las 3:00 PM y salida hasta las 11:00 AM. ¿Llegas temprano o te vas tarde? Escríbenos — somos flexibles cuando el calendario lo permite." },
        { q: "¿La piscina es realmente privada?", a: "Sí — la piscina es solo tuya, abierta 24/7. Sin compartir, sin horarios, sin extraños. Los baños de medianoche están permitidos." },
        { q: "¿Qué tan lejos está la playa?", a: "A unos 5 minutos. La bahía de Cabarete para nadar y cenar sobre la arena, y Kite Beach para el viento famoso mundial — ambas a un paseo de distancia." },
        { q: "¿La zona es segura?", a: "La villa está dentro de una comunidad cerrada y tranquila, con cámaras exteriores de seguridad y acceso controlado. La mayoría de los huéspedes duermen con las ventanas abiertas." },
        { q: "¿Cómo funcionan los pagos?", a: "Pagas el monto total en línea mediante el pago seguro de Stripe — rápido, cifrado y sin nada pendiente a la llegada. Los pagos no son reembolsables — todos los detalles están en nuestros Términos y Condiciones." },
        { q: "¿Pueden organizar el traslado del aeropuerto?", a: "Sí — el aeropuerto de Puerto Plata (POP) está a unos 25 minutos. Envíanos tu vuelo por correo y tendremos un conductor de confianza esperándote." },
        { q: "¿Necesito experiencia para el kitesurf?", a: "Para nada. Cabarete es donde aprenden los principiantes — nuestras clases de descubrimiento te emparejan con instructores certificados y todo el equipo está incluido." },
        { q: "¿El Wi-Fi sirve para trabajar remoto?", a: "Sí — internet de fibra rápido y un espacio de trabajo dedicado. Muchos huéspedes trabajan por la mañana y hacen kite por la tarde." },
        { q: "¿Podemos reservar el chef privado o un masaje?", a: "Claro — Island Table (chef privado) e Island Serenity (masaje) están disponibles en la villa. Solicítalos en la página de Servicios Isleños y te responderemos con una cotización." },
        { q: "¿Qué pasa si surge algo y no podemos viajar?", a: "Escríbenos lo antes posible. Los pagos no son reembolsables, pero si logramos volver a alquilar tus fechas, con gusto te ofrecemos mover tu estadía una vez." },
      ],
    },
    terms: {
      caption: "La letra pequeña",
      title: "Términos y Condiciones",
      updated: "Última actualización: octubre de 2026",
      intro: "Bienvenido a Villa Paraiso. Al reservar una estadía, excursión o servicio isleño a través de este sitio, aceptas los siguientes términos, que existen para mantener la villa hermosa y a cada huésped feliz.",
      sections: [
        { h: "1. Reservas y pagos", b: "Una reserva queda confirmada cuando se recibe el monto total mostrado al pagar, a través de nuestro procesador seguro (Stripe). Los pagos NO SON REEMBOLSABLES bajo ninguna circunstancia, incluyendo cambios de planes, interrupciones de viaje, enfermedad o clima. Las tarifas se cotizan en dólares estadounidenses." },
        { h: "2. Cancelaciones y cambios de fecha", b: "Como las fechas se reservan exclusivamente para ti, las cancelaciones pierden el monto pagado. Se puede ofrecer un único cambio de fecha, a nuestra discreción, si se solicita al menos 14 días antes de la llegada y según disponibilidad. Las ausencias sin aviso se cobran en su totalidad." },
        { h: "3. Daños, multas y seguridad", b: "Los huéspedes son financieramente responsables de cualquier daño, rotura o desorden excesivo causado durante la estadía, más allá del desgaste normal. Los costos de reparación o reposición se facturarán al huésped, y puede aplicarse una multa de hasta USD 250 por negligencia grave o incumplimiento de las reglas de la casa. Cámaras exteriores de seguridad vigilan las entradas de la propiedad para la seguridad de todos; no existen cámaras dentro de la casa." },
        { h: "4. Ocupación, eventos y ruido", b: "La ocupación máxima es de 6 huéspedes registrados. Fiestas, eventos y visitantes no registrados requieren aprobación previa por escrito. La villa está dentro de una comunidad cerrada y tranquila — respeta las horas de silencio de 10:00 PM a 8:00 AM. Los incumplimientos pueden terminar la estadía sin reembolso." },
        { h: "5. Uso de la piscina y la propiedad", b: "La piscina no tiene salvavidas y se usa completamente bajo tu propio riesgo. Los niños deben estar supervisados en todo momento. Prohibido lanzarse de cabeza y llevar vidrio cerca de la piscina. Fumar solo está permitido al aire libre; fumar dentro conlleva una multa de limpieza de USD 250." },
        { h: "6. Excursiones y servicios isleños", b: "Las excursiones, el chef y los masajes pueden ser operados por terceros independientes. Villa Paraiso los gestiona de buena fe, pero no es responsable de su operación, cambios de horario o cancelaciones por clima; en esos casos, los montos pagados a nosotros por el servicio afectado se reembolsan o acreditan." },
        { h: "7. Responsabilidad", b: "Villa Paraiso no es responsable por pérdidas, robos, lesiones o accidentes en la propiedad o durante las actividades, salvo negligencia grave comprobada de nuestra parte. Los huéspedes son responsables de su seguro de viaje, documentos y pertenencias." },
        { h: "8. Fuerza mayor", b: "No somos responsables por fallas causadas por eventos fuera de nuestro control — tormentas, cortes de electricidad o internet, o restricciones gubernamentales. Siempre trabajaremos contigo para reprogramar o acreditar los servicios afectados." },
        { h: "9. Privacidad y contacto", b: "Tus datos se usan solo para gestionar tu reserva y comunicarnos contigo — nunca se venden ni se comparten. Estos términos se rigen por las leyes de la República Dominicana. ¿Preguntas? Escríbenos a villaparaisocabarete@gmail.com." },
      ],
    },
    services: {
      caption: "Servicios isleños",
      title: "Un poco más de isla, preparado para ti.",
      copy: "Dos lujos tranquilos, disponibles en la villa. Todo tiene precio bajo petición — envíanos tus fechas y te responderemos personalmente con una cotización.",
      priceNote: "Precio bajo petición",
      teaserCaption: "En la villa",
      teaserCta: "Explora los servicios isleños",
      guestsSuffix: "huéspedes",
      requestBtn: "Solicitar esta experiencia",
      chef: {
        name: "Mesa Isleña — Chef Privado",
        tagline: "Déjate llevar por la vida isleña mientras tu chef privado se encarga de la cocina.",
        gatheringLbl: "Tu grupo",
        experienceLbl: "Elige tu experiencia",
        gatherings: { two: "Mesa para Dos", together: "Sabores y Compañía", gathering: "Reunión Isleña" },
        experiences: {
          breakfast: "Amanecer Caribeño — Solo desayuno",
          lunch: "Mediodía en el Paraíso — Solo almuerzo",
          dinner: "Noche Tropical — Solo cena",
          fullday: "Un Día Completo de Sabores Isleños — Desayuno, almuerzo y cena",
        },
      },
      massage: {
        name: "Serenidad Isleña — Masaje",
        tagline: "Baja el ritmo, relájate y disfruta de un poco de calma caribeña.",
        durationLbl: "Elige tu pausa",
        durations: { pause: "Pausa Isleña · 30 minutos", escape: "Escape al Paraíso · 1 hora" },
      },
      modal: {
        title: "Solicitar servicio isleño",
        date: "Fecha preferida",
        name: "Tu nombre",
        namePh: "Nombre completo",
        email: "Correo electrónico",
        phone: "Teléfono (opcional)",
        notes: "¿Algo que debamos saber?",
        notesPh: "Alergias, celebraciones, preferencias…",
        submit: "Enviar solicitud",
        sending: "Enviando…",
        doneTitle: "Solicitud enviada",
        doneCopy: "Los anfitriones te responderán personalmente con una cotización — normalmente en un día. Una confirmación va camino a tu correo.",
        summary: "Tu solicitud",
        close: "Listo",
        total: "Total",
        payFull: "Pagar en línea — ${amount}",
        orWait: "O simplemente espera — los anfitriones confirmarán por correo y aún no se cobra nada.",
      },
    },
    availability: {
      caption: "Disponibilidad y tarifas",
      titleA: "Las mejores fechas",
      titleB: "vuelan.",
      perNight: "/ noche",
      minStay: "Estadía mínima de {n} noches",
      minStay1: "Estadía mínima: 1 noche",
      depositLine: "Paga el total en línea para asegurar tus fechas",
      crossedOut: "Las fechas tachadas ya están reservadas",
      cta: "Consulta por tus fechas",
      from: "desde",
      seasonal: "Tarifas de temporada aplican en fechas seleccionadas",
    },
    homeExp: {
      caption: "Experiencias",
      title: "Aventuras reservadas con nosotros, con recogida en tu puerta.",
      viewAll: "Ver todas las experiencias",
    },
    dining: {
      caption: "Comer & beber",
      title: "Nuestra mesa está reservada. La tuya también debería.",
      viewAll: "La guía completa",
    },
    homeBlog: {
      caption: "Lo nuevo",
      title: "Notas de la playa — vida nocturna, viento, cascadas.",
      viewAll: "Leer el diario",
    },
    cta: {
      titleA: "¿Listo para la",
      titleB: "hora dorada?",
      sub: "Envíanos tus fechas y te respondemos con disponibilidad, tarifa y un plan para tu primer atardecer.",
      enquire: "Consulta por la villa",
      dine: "Dónde cenar esta noche",
    },
    footer: {
      tagline: "Un refugio caribeño privado en Cabarete, República Dominicana — con cada excursión, mesa y atardecer organizado por tu anfitrión.",
      explore: "Explorar",
      contact: "Contacta al anfitrión",
      rights: "Villa Paraiso. Todos los derechos reservados.",
      quote: "Lujo descalzo, al borde del Atlántico.",
      faq: "Preguntas frecuentes",
      terms: "Términos y Condiciones",
    },
    excursions: {
      caption: "Experiencias",
      titleA: "Elige la aventura.",
      titleB: "Nosotros nos encargamos del resto.",
      copy: "Cada experiencia la operan personas que conocemos personalmente y se reserva directamente con tu anfitrión. Elige fecha y hora, y te recogemos en la entrada de Villa Paraiso — o en tu hotel si visitas desde otro lugar de Cabarete.",
      loading: "Cargando experiencias…",
      all: "Todas",
      noteTitle: "¿Te hospedas en Villa Paraiso?",
      noteCopy: "Cada excursión incluye coordinación puerta a puerta: confirmamos el operador, la hora de recogida llega a tus mensajes y alguien te espera en la entrada de la villa cuando llega la van. Sin logos, sin filas, sin energía de bus turístico.",
      book: "Reservar esta experiencia",
      perPerson: "/ persona",
    },
    restaurants: {
      caption: "Comer & beber · Cabarete",
      titleA: "Adonde te llevaríamos,",
      titleB: "si fueras de la familia.",
      copy: "Sin espacios pagados — solo las mesas a las que volvemos una y otra vez, desde ranchos de mariscos con piso de arena hasta cenas elegantes a la luz de las velas. Pídele al anfitrión la recomendación de esta noche y llamamos por ti.",
      loading: "Poniendo las mesas…",
      all: "Todos",
      order: "Pide:",
      directions: "Cómo llegar",
    },
    blog: {
      caption: "Lo nuevo",
      titleA: "El diario de",
      titleB: "Cabarete.",
      copy: "Qué hay, qué abre y de dónde sopla el viento — escrito por tu anfitrión, actualizado durante la temporada.",
      loading: "Buscando lo último…",
      readStory: "Leer la historia →",
      back: "El diario",
      keepReading: "Sigue leyendo",
      notFound: "Esta historia se fue mar adentro.",
      notFoundBack: "← Volver al diario",
      opening: "Abriendo la historia…",
    },
    auth: {
      signIn: "Entrar",
      createAccount: "Crear cuenta",
      name: "Nombre completo",
      namePh: "Tu nombre",
      email: "Correo electrónico",
      password: "Contraseña",
      passwordPh: "6+ caracteres",
      guestAccount: "Cuenta de huésped",
      submitLogin: "Entrar",
      submitRegister: "Crear cuenta",
      google: "Continuar con Google",
      orDivider: "o con correo",
      wait: "Un momento…",
      ownerNote: "¿Eres el dueño de la villa? Entra con tu cuenta de administrador para abrir el panel.",
      backHome: "← Volver a la villa",
      welcomeBack: "Bienvenido de vuelta a la villa",
      accountCreated: "Cuenta creada — bienvenido a Villa Paraiso",
      sideCaption: "Miembros de la casa",
      sideTitle: "Entra para reservar aventuras y guardar cada atardecer en un solo lugar.",
    },
    booking: {
      title: "Reserva tu experiencia",
      bookedTitle: "Reserva lista",
      date: "Fecha",
      guests: "Huéspedes",
      timeSlot: "Horario",
      pickup: "Lugar de recogida",
      villaOption: "Villa Paraiso",
      villaOptionSub: "Te recogemos justo en la entrada principal de la villa.",
      customOption: "Hotel / otro lugar",
      customOptionSub: "¿Te hospedas en un hotel o resort en Cabarete? Vamos por ti.",
      yourHotel: "Tu hotel / punto de recogida",
      hotelPh: "ej. Hotel Velero Beach, lobby principal",
      contactName: "Nombre de contacto",
      contactNamePh: "Huésped principal",
      phone: "Teléfono",
      notes: "Peticiones especiales",
      notesPh: "Almuerzo vegetariano, nadador nervioso, sorpresa de cumpleaños…",
      summary: "Resumen",
      seasonUplift: "Precio de {season} +{pct}%",
      orDepositNow: "o paga ${amount} en su totalidad ahora",
      nothingCharged: "Aún no se cobra nada — al confirmar puedes pagar el monto total en línea, de forma segura.",
      continue: "Continuar",
      back: "Atrás",
      confirm: "Confirmar reserva",
      confirming: "Confirmando…",
      pass: "Pase de aventura",
      ref: "Ref",
      dateTime: "Fecha y hora",
      guestsLbl: "Huéspedes",
      people: "personas",
      person: "persona",
      pickupLbl: "Recogida",
      total: "Total",
      statusPending: "pendiente — el anfitrión confirma pronto",
      emailNote: "Un correo de confirmación va en camino para ti y el anfitrión. Asegura tu lugar pagando en línea el total.",
      payDeposit: "Pagar todo — ${amount}",
      openingCheckout: "Abriendo el pago…",
      whatsapp: "WhatsApp al anfitrión",
      viewTrips: "Ver mis viajes",
      done: "Listo",
      errDate: "Elige una fecha para tu aventura",
      errTime: "Elige un horario",
      errHotel: "Dinos tu hotel o punto de recogida",
      errContact: "Necesitamos un nombre de contacto y teléfono",
      signinToBook: "Inicia sesión para completar tu reserva",
    },
    enquiry: {
      title: "Reserva la Villa",
      doneTitle: "Consulta enviada",
      intro: "Elige tus fechas y reserva al instante — o envíanos una nota si prefieres un trato personal primero.",
      name: "Nombre completo",
      namePh: "Tu nombre",
      email: "Correo electrónico",
      checkin: "Llegada",
      checkout: "Salida",
      guests: "Huéspedes",
      phone: "Teléfono (opcional)",
      notes: "¿Algo que debamos saber?",
      notesPh: "Viaje de aniversario, recogida en el aeropuerto, noches de chef…",
      send: "Enviar consulta",
      sending: "Enviando…",
      nightsTimes: "{nights} noches × ${rate}/noche",
      overlap: "Estas fechas se cruzan con una reserva existente — elige otras.",
      belowMin: "La estadía mínima es de {n} noches.",
      thanks: "Gracias, {name}.",
      successCopy: "está con el anfitrión. Un correo de confirmación va en camino a",
      guestsWord: "huéspedes",
      nightsWord: "noches",
      estimated: "Total estimado de la estadía:",
      whatsapp: "WhatsApp al anfitrión",
      backToParadise: "Volver al paraíso",
      errDates: "Elige tus fechas de llegada y salida",
      errOrder: "La salida debe ser después de la llegada",
      errMin: "La estadía mínima es de {n} noches",
      errOverlap: "Esas fechas se cruzan con una reserva existente — prueba con otras",
      waText: "¡Hola Villa Paraiso! Acabo de enviar una consulta por la villa.",
    },
    dashboard: {
      caption: "Mis viajes",
      hola: "Hola,",
      sub: "Cada aventura y consulta, en un solo lugar soleado.",
      loading: "Cargando tus viajes…",
      bookingsTitle: "Reservas de excursiones",
      emptyBookings: "Aún no hay aventuras. Las cascadas te esperan.",
      browse: "Explorar experiencias",
      enquiriesTitle: "Consultas de la villa",
      emptyEnquiries: "Aún no hay consultas. Tus fechas, nuestra piscina.",
      seeVilla: "Ver la villa",
      cancel: "Cancelar",
      cancelled: "Reserva cancelada — el anfitrión ha sido notificado",
      payDeposit: "Pagar todo — ${amount}",
      payStayDeposit: "Pagar estadía completa — ${amount}",
      openingCheckout: "Abriendo el pago…",
      depositPaid: "Pagado",
      depositRefunded: "Reembolsado",
      chatHost: "Chatear con el anfitrión",
      guests: "huéspedes",
      nights: "noches",
      sent: "enviada",
      estimate: "Estimado",
      waBooking: "¡Hola Villa Paraiso! Sobre mi reserva {ref} — {title} el {date}.",
      waStay: "¡Hola Villa Paraiso! Sobre mi estadía {checkin} → {checkout}.",
    },
    reviews: {
      caption: "Libro de huéspedes",
      title: "Amada por quienes se quedaron.",
      subtitle: "Notas de huéspedes anteriores — sin editar, aprobadas por el anfitrión.",
      share: "Comparte tu experiencia",
      shareTitle: "Deja una reseña",
      yourRating: "Tu calificación",
      yourReview: "Tu reseña",
      reviewPh: "La piscina a la hora dorada, las recomendaciones del anfitrión…",
      submit: "Enviar reseña",
      thanks: "¡Gracias! Tu reseña aparecerá cuando el anfitrión la apruebe.",
      hostReply: "Respuesta de tu anfitrión",
      addPhoto: "Agrega una foto (opcional)",
      uploadingPhoto: "Subiendo foto…",
      photoReady: "Foto adjunta",
      removePhoto: "Quitar",
      needStay: "Las reseñas son para huéspedes con una reserva o consulta de estadía.",
    },
    payment: {
      checking: "Confirmando tu pago…",
      checkingSub: "Hablando con el proveedor de pagos. Un momento.",
      paidA: "Pago",
      paidB: "recibido.",
      paidSub: "Tu reserva está asegurada. Un recibo va en camino a tu correo y tu anfitrión ha sido notificado.",
      pendingTitle: "Casi listo",
      pendingSub: "El pago sigue procesándose. Normalmente llega en un minuto — revisa \"Mis viajes\" en breve y te escribiremos cuando se confirme.",
      cancelledTitle: "No se hizo ningún cargo",
      cancelledSub: "Cancelaste el pago. Tu reserva sigue guardada — puedes pagar cuando quieras desde \"Mis viajes\".",
      failedTitle: "No encontramos ese pago",
      failedSub: "El enlace de la sesión parece incompleto. Revisa el estado de tu pago en \"Mis viajes\".",
      viewTrips: "Ver mis viajes",
      goTrips: "Ir a mis viajes",
      backTrips: "Volver a mis viajes",
    },
    cart: {
      title: "Tu carrito",
      empty: "Tu carrito está vacío",
      emptySub: "Agrega una excursión, un servicio o una estadía en la villa — te esperará aquí.",
      browse: "Ver experiencias",
      addBtn: "Agregar al carrito",
      added: "Agregado a tu carrito",
      removed: "Quitado del carrito",
      guests: "Huéspedes",
      quote: "Precio a solicitud",
      approval: "Aprobación del anfitrión",
      subtotal: "Total a pagar hoy",
      feesNote: "Sin cargos extra — el precio que ves es el precio que pagas.",
      requestsNote: "Los servicios a solicitud se envían al anfitrión al pagar — hoy no se cobran.",
      checkout: "Pago seguro",
      checkingOut: "Abriendo el pago seguro…",
      bookNow: "Reservar ahora",
      sendRequests: "Enviar solicitudes",
      requestsSent: "Solicitudes enviadas — el anfitrión te responderá por correo",
      signin: "Inicia sesión para pagar",
      phone: "Teléfono (para el anfitrión)",
      unavailable: "No disponible",
      continueShopping: "Seguir explorando",
      orderTitle: "Tu pedido",
      villaStay: "Estadía en Villa Paraiso",
    },
    food: {
      caption: "Cocina privada",
      title: "Cenas Privadas y Servicios de Comida",
      intro: "Disfruta de comidas recién preparadas en la comodidad de Villa Paraiso. Desde desayunos relajados hasta cenas dominicanas y BBQ privados — déjanos cocinar mientras tú disfrutas tu estadía.",
      perPerson: "por persona",
      perPersonDay: "por persona, por día",
      quote: "Solicitar cotización",
      conditionsTitle: "Bueno saber",
      c1: "Cargo mínimo equivalente a cuatro huéspedes por comida o paquete diario.",
      c2: "Incluye ingredientes estándar, preparación, servicio y limpieza de la cocina.",
      c3: "Alcohol, ingredientes premium y solicitudes especiales tienen costo extra y requieren cotización confirmada.",
      c4: "Las solicitudes deben enviarse con al menos 48 horas de anticipación.",
      c5: "Todos los servicios están sujetos a disponibilidad y confirmación del anfitrión.",
      c6: "Informa alergias y requisitos dietéticos con anticipación — solo podemos adaptarnos a lo que conocemos.",
      c7: "Cualquier impuesto o cargo obligatorio se detalla en tu cotización confirmada — antes de cualquier pago.",
      requestBtn: "Solicitar Servicio de Comida",
      modalTitle: "Solicitar servicio de comida",
      name: "Nombre completo",
      email: "Correo electrónico",
      whatsapp: "Número de WhatsApp",
      ref: "Referencia de reserva (si la tienes)",
      service: "Servicio o paquete",
      dateSingle: "Fecha del servicio",
      dateFrom: "Primer día",
      dateTo: "Último día",
      times: "Horarios preferidos",
      adults: "Adultos",
      children: "Niños",
      allergies: "Alergias, preferencias dietéticas y solicitudes especiales",
      estimate: "Total estimado",
      minNote: "Aplica el mínimo de cuatro huéspedes — se factura por {billed} huéspedes.",
      quoteReq: "Requiere cotización — confirmaremos el precio por correo.",
      submit: "Enviar solicitud",
      sending: "Enviando…",
      received: "Hemos recibido tu solicitud de servicio de comida. Confirmaremos disponibilidad, menú y precio final antes del pago.",
      done: "Solicitud recibida",
      addMeals: "Agregar comidas a tu estadía",
      requestFor: "Solicitar servicio de comida",
      advanceNote: "Con al menos 48 horas de anticipación, por favor.",
      back: "Volver",
      guestsSuffix: "huéspedes",
    },
  },
};
